import {Spinner, SpinnerSize} from '@blueprintjs/core';
import React from 'react';
import {WithTranslation} from 'react-i18next';
import {services} from '../../../../application/service/services';
import {TableDataset, TableDatasetDisplay} from '../../../../component/table/Table';
import {VisualizationInstance} from '../../../model/viz/VisualizationInstance';
import './tableViz.scss';
import {ResolvedParams} from "../../../model/params/resolve/ResolvedParams";
import {ClickableViz} from "../ClickableViz";
import {FieldValue} from "../../../../application/model/field/FieldValue";
import {
    FieldVizFeedbacksQueryContext,
    MultiFieldsVizFeedbacksQueryContext,
    VizFeedbacksQueryContext
} from "../../../model/viz/VizFeedbacksQueryContext";
import {AggregationRenderer, labelHeader} from "../../../../component/table/TableRenderers";
import {I18nLabel} from "../../../../application/service/i18nService";
import {Label} from "../../../model/model";
import {SortOrder, SortOrderEnum} from "../../../../utils/query/SortOrder";
import {variableResolverService} from "../../../service/variableResolverService";
import _ from "lodash";


export interface ColumnDefinition {
    name: string,
    "@type": string,
    title?: Label
}

export interface UIColumn {
    compareFunction?: (columnName: string, direction: SortOrder) => (item1, itemB) => number;
    sortable: boolean;
    valueGetters?: { [key: string]: (item) => any }
}

export type ColumnRenderer = Partial<TableDatasetDisplay<any>> & Partial<ColumnDefinition> & Partial<UIColumn> & {
    [key: string]: any
};
export type ColumnRenderers = { [key: string]: ColumnRenderer };


export type Column = {
    columnName: string,
    headerRenderer?: (column: TableDatasetDisplay<any>, columnIndex, currentOrder, sorthandler: (columnName: string, columnIndex: number, sortDirection: SortOrder, event?) => void) => React.JSX.Element;
    dataRenderer: (rowValue, column: TableDatasetDisplay<any>) => React.JSX.Element;
    style?: React.CSSProperties
} & ColumnDefinition & UIColumn;

export interface TableValue {
    name: string;
    value: any;
}

export interface TableItem {
    data: TableValue[];
    children: Array<TableItem>;
    totalCount: number;
    fieldValue: string;
    field: string;
    childrenCount: number;
}

export interface TableItemUI extends TableItem {
    children: Array<TableItemUI>;
    parent: TableItemUI | null;
}

export interface TableData {
    totalCount: number;
    items: Array<TableItem>;
}

export interface TableUIData extends TableData {
    totalCount: number; //nb of document concerned by the request
    childrenCount: number; //nb of children including other count (children is not necessarily documents)
    items: Array<TableItemUI>;
}

export interface AbstractTableVizStateProps {
    clickItem?: TableItemUI;
    inProgress: boolean,
    data?: TableUIData,
    sorting?: { columnName: string, direction: SortOrder },
    top?: { columnName: string, count: number, type: 'TOP' | 'FLOP' } | null
};


export interface AbstractTableVizProps extends WithTranslation {
    clickItem?: any;
    viz: VisualizationInstance,
    dashboardResolvedParams: ResolvedParams,
    openFeedbacksPanel: (viz: ClickableViz, panelTitle: string) => void
}


export abstract class AbstractTableVizComponent<T extends AbstractTableVizProps> extends React.PureComponent<T> implements ClickableViz {

    protected columns: { [key: string]: Column } = {};

    abstract getColumnRender(type: string): ColumnRenderer;

    abstract renderTable(uiParams: any) ;

    static defaultCompareFunction = (valueGetter: (data: any) => any) => {
        return (columnName: string, direction: SortOrder) => {
            return (line1, line2) => {
                const line1Value = line1.data.filter(columnValue => columnValue.name === columnName).map(valueGetter).find(() => true);
                const line2Value = line2.data.filter(columnValue => columnValue.name === columnName).map(valueGetter).find(() => true);
                let result = 0;
                if (_.isNil(line1Value) && _.isNil(line2Value)) result = 0;
                if (_.isNil(line1Value)) result = -1;
                if (_.isNil(line2Value)) result = 1;
                if (!_.isNil(line1Value) && !_.isNil(line2Value)) {
                    result = line1Value - line2Value;
                }
                return result * (direction == SortOrderEnum.ASC ? 1 : -1);
            }
        }
    }
    static getColumnLabelKey = (reportId: string, vizDefinition: any, columnName: string) => {
        return `report.${reportId}.viz.${vizDefinition.id}.column.${columnName}.title`;
    }

    static getDerivedStateFromProps = (props, state: AbstractTableVizStateProps) => {
        const {data} = props;
        return {...state, data: data};
    };


    static enrichItems(items, parent) {
        const enrichedItems = [];
        items.forEach(item => {
            const itemClone = {...item};
            itemClone.parent = parent;
            if (item.children?.length > 0) {
                itemClone.children = AbstractTableVizComponent.enrichItems(item.children, item);
            }
            enrichedItems.push(itemClone);
        })
        return enrichedItems;
    }

    static declareColor(data: TableData) {
        data.items.forEach(item => {
            this.declareColorForItem(item);
        });
    }

    private static declareColorForItem(item: TableItem) {
        services.getColorService().registerFieldAndValue(item.field, item.fieldValue);
        item.children?.forEach(item => {
            this.declareColorForItem(item);
        });
    }

    private getTopXLeaves(tree: Array<TableItemUI>, top: number, compareFunctions: ((itemA, itemB) => number)[]): TableItemUI[] {
        const leaves: TableItemUI[] = this.getLeaves(tree);
        const compareFunction = this.wrappedComparator(compareFunctions);
        // Tri des feuilles en fonction de la valeur de "count"
        const sortedLeaves = leaves.sort(compareFunction);
        // Récupération des trois premières feuilles
        const topXLeaves = sortedLeaves.slice(0, top);
        return this.getTreeFromLeaves(tree, topXLeaves);

    }

    private wrappedComparator(compareFunctions: ((itemA, itemB) => number)[]) {
        let compareFunction = compareFunctions.shift();
        if (compareFunctions.length > 0) {
            const nextLevelComparator = this.wrappedComparator(compareFunctions)
            return (itemA, itemB) => {
                let comparatorResult = compareFunction(itemA, itemB);
                if (comparatorResult == 0) {
                    comparatorResult = nextLevelComparator(itemA, itemB);
                }
                return comparatorResult;
            }
        }
        return compareFunction;
    }

    private getTreeFromLeaves(tree: Array<TableItemUI>, topXLeaves: TableItemUI[]): Array<TableItemUI> {
        const newTree: Array<TableItemUI> = [];
        tree.forEach(item => {
            if (item.children && item.children.length > 0) {
                const childTree = this.getTreeFromLeaves(item.children, topXLeaves);
                if (childTree && childTree.length > 0) {
                    newTree.push({...item, children: childTree});
                }
            } else if (topXLeaves.indexOf(item) >= 0) {
                newTree.push(item);
            }
        })
        return newTree;
    }

    getLeaves(tree: Array<TableItemUI>): TableItemUI[] {
        const leaves: TableItemUI[] = [];
        tree.forEach(value => {
            if (!value.children || value.children.length === 0) {
                // Si le nœud n'a pas d'enfants, il est une feuille
                leaves.push(value);
                return;
            }
            // Récupération des feuilles de chaque enfant récursivement
            this.getLeaves(value.children).forEach(childItem => leaves.push(childItem));
        });
        return leaves;
    }

    state: AbstractTableVizStateProps = {
        inProgress: false,
        sorting: null,
        top: null
    };

    setData(data) {
        this.setState({inProgress: false, data: data});
    }

    containsLevels() {
        return this.state.data.items.filter(item => item.children && item.children.length > 0).length > 0;
    }

    onSort(columnName: string, direction: SortOrder) {
        this.setState({...this.state, sorting: {columnName, direction}});
    }

    buildColumns(): Array<Column> {
        const {viz, t} = this.props;
        const params = viz.definition.params;

        const aggregationColumnsToDisplay = [];

        const columns = this.props.viz.definition.params.columns as Array<ColumnDefinition>;

        columns
            .filter(value => value["@type"] === "aggregation")
            .forEach(aggregation => {
            const field = services.getFieldsService().getField(aggregation['field']);
            aggregationColumnsToDisplay.push({
                ...aggregation,
                columnName: aggregation['field'],
                style: aggregation['style'] ? aggregation['style'] : {flex: 2, textAlign: 'left'},
                headerRenderer: labelHeader(field ? field.getLabel(t) : aggregation['field'], true),
                dataRenderer: AggregationRenderer,
                compareFunction: (columnName: string, direction: SortOrder) => {
                    return (line1, line2) => {
                        if (line1.field !== columnName) return 0;
                        const field = services.getFieldsService().getField(line1.field);
                        const fieldValues = services.getFieldsService().getFieldValues(field.name);

                        const line1Value = fieldValues && fieldValues.find(line1.fieldValue) ? fieldValues.find(line1.fieldValue).getLabel(t) : line1.fieldValue;
                        const line2Value = fieldValues && fieldValues.find(line2.fieldValue) ? fieldValues.find(line2.fieldValue).getLabel(t) : line2.fieldValue;
                        let result = 0;
                        if (line1Value < line2Value) {
                            result = -1;
                        }
                        if (line1Value > line2Value) {
                            result = 1;
                        }
                        return result * (direction == SortOrderEnum.ASC ? 1 : -1);
                    }
                }
            })
        });

        const columnsToDisplay = columns
            .filter(value => value["@type"] !== "aggregation")
            .map(col => {
                let isVisible = this.props.viz.definition.uiParams.columns?.[col.name]?.visible
                if (isVisible) {
                    const visibleAsString = variableResolverService.resolveParamInString(isVisible, this.props.dashboardResolvedParams.valueAsPlainObject());
                    const visible = visibleAsString ? eval(visibleAsString) : true;
                    if (!visible) return null;
                }

                const colDef = {...this.getColumnRender(col['@type']), columnName: col.name, ...col};
                if (col.title) {
                    const titleKey = col.title?.key ? col.title.key : AbstractTableVizComponent.getColumnLabelKey(this.props.viz.dashboardId, this.props.viz.definition, col.name)
                    const headerRender = labelHeader(titleKey, colDef.sortable, col.title);
                    colDef.headerRenderer = headerRender;
                }
                if (!colDef) throw new Error("Not column definition found for : " + col.name)
                return colDef as TableDatasetDisplay<any>;
            }).filter(value => !!value);

        return (aggregationColumnsToDisplay || []).concat(columnsToDisplay);
    }

    protected getUiDataset(): TableDataset<TableItemUI> {
        const {viz, t} = this.props;
        const params = viz.definition.params;

        const cols = this.buildColumns();
        cols.forEach(col => this.columns[col.columnName] = col);
        let itemsToDisplay = (viz.data as TableUIData).items;
        if (viz.definition.params.top) {
            const top = viz.definition.params.top;
            const compareFunctions = [];
            (top.columns as (string | Partial<{ name: string, sortOn?: string }>)[]).forEach(column => {
                let compareFunction = null;
                let columnName: string = null;
                if (typeof column === 'string') {
                    compareFunction = this.columns[column].compareFunction;
                    columnName = column;
                } else {
                    columnName = column.name;
                    if (this.columns[column.name].compareFunction) {
                        compareFunction = this.columns[column.name].compareFunction;
                    } else if (column.sortOn) {
                        if (!this.columns[column.name].valueGetters || !this.columns[column.name].valueGetters[column.sortOn]) {
                            throw new Error("Not value getter found for column " + column.name);
                        }
                        compareFunction = AbstractTableVizComponent.defaultCompareFunction(this.columns[column.name].valueGetters[column.sortOn]);
                    }
                }
                compareFunctions.push(compareFunction(columnName, top.type == 'TOP' ? SortOrderEnum.DESC : SortOrderEnum.ASC));
            })
            itemsToDisplay = this.getTopXLeaves(itemsToDisplay, top.count, compareFunctions);
        }

        if (this.state.sorting) {
            itemsToDisplay = this.sort(itemsToDisplay);
        }

        return {
            data: _.cloneDeep(itemsToDisplay),
            display: cols
        };
    }

    private sort(items: Array<any>) {
        const {viz, dashboardResolvedParams} = this.props;
        const column = this.columns[this.state.sorting.columnName];
        const compareFunction = column.compareFunction(this.state.sorting.columnName, this.state.sorting.direction);
        const sortedItems = [...items].sort(compareFunction).map(item => {
            return {...item}
        });
        sortedItems.forEach(item => {
            if (item.children) {
                item.children = this.sort(item.children);
            }
        })
        return sortedItems;
    }

    render() {
        const {t, viz} = this.props;
        const {inProgress} = this.state;
        const uiParams = viz.definition.uiParams;

        return <>
            <div className="tableViz">
                <div className="tableViz-main">
                    {
                        inProgress ? <Spinner size={SpinnerSize.SMALL}/> : this.renderTable(uiParams)
                    }
                </div>
            </div>
        </>;
    }

    getClickedFieldValue(): FieldValue {
        if (this.state.clickItem) {
            const {viz} = this.props;
            return services.getFieldsService().findFieldValue(viz.definition.params.field, this.state.clickItem.fieldValue);
        }
        return null;
    }

    getFeedBacksQueryContext(): VizFeedbacksQueryContext {
        const result = new MultiFieldsVizFeedbacksQueryContext();
        if (this.state.clickItem) {
            const {viz} = this.props;
            result.fields = this.getFieldsFromItem(this.state.clickItem);
        }
        return result;
    }

    private getFieldsFromItem(clickItem: TableItemUI): Array<FieldVizFeedbacksQueryContext> {
        const result = [];
        if (clickItem.parent) {
            result.push(...this.getFieldsFromItem(clickItem.parent));
        }
        const field = new FieldVizFeedbacksQueryContext();
        field.field = clickItem.field;
        field.selectedValue = clickItem.fieldValue;
        result.push(field);
        return result;
    }

    public static getLabels(report: any, vizDefinition: any): Array<I18nLabel> {
        const labels: Array<I18nLabel> = [];
        vizDefinition.params.columns.forEach(column => {
            if (column.title && column.title.labels) {
                Object.entries(column.title.labels)
                    .forEach(([lang, label]) => labels.push({
                        key: AbstractTableVizComponent.getColumnLabelKey(report.id, vizDefinition, column.name),
                        value: label as string,
                        lang: lang
                    }))
            }
        })
        return labels;
    }

}


